import { useEffect } from 'react';
import useHasAcknowledgedTerms from 'src/hooks/useHasAcknowledgedTerms';
import useModal from 'src/components/Modals/hooks/useModal';
import BaseModal from 'src/components/primitives/BaseModal/BaseModal';
import Button from 'src/components/primitives/Button/Button';
import Link from 'src/components/primitives/Link/Link';
import Env from 'src/environmentVariables/EnvironmentVariables';
import { useDisconnectWallets } from 'src/hooks/wallets/useDisconnectWallets';
import useRootAuthorityUserStatsAccount from 'src/hooks/query/authority/useRootAuthorityUserStatsAccount';
import { useSpotMarketsLookup } from 'src/hooks/spotMarkets/useSpotMarketsLookup';
import { useSearchParams } from 'next/navigation';

export default function TermsOfUseModal() {
	const [hasAcknowledgedTerms, updateHasAcknowledgedTerms] =
		useHasAcknowledgedTerms();
	const { data: userStatsAccount, isLoading: isLoadingUserStats } =
		useRootAuthorityUserStatsAccount();
	const { show, hide } = useModal();
	const disconnectWallets = useDisconnectWallets();
	const searchParams = useSearchParams();
	const spotMarketLookup = useSpotMarketsLookup();
	const token = searchParams?.get('token');
	const spotMarketConfig = spotMarketLookup.find(
		(market) => market?.symbol.toLowerCase() === token?.toLowerCase()
	);

	const handleReject = async () => {
		await disconnectWallets();
		hide();
	};

	const handleAccept = () => {
		updateHasAcknowledgedTerms();
		hide();

		const hasUserStatsAccount = userStatsAccount !== undefined;

		// After accepting terms, check userStats account and show appropriate modal
		if (!isLoadingUserStats && !hasUserStatsAccount) {
			// New user - show deposit modal
			show({
				type: 'deposit',
				props: {
					defaultLargestBalance: spotMarketConfig ? false : true,
					collateralIndex: spotMarketConfig?.marketIndex,
				},
			});
		}
	};

	useEffect(() => {
		if (
			hasAcknowledgedTerms.hasPreviouslyAcknowledgedTerms &&
			!hasAcknowledgedTerms.shouldShowTermsReminder
		) {
			hide();
		}
	}, [hasAcknowledgedTerms, hide]);

	return (
		<BaseModal.Container onClose={handleReject} dataTestId="terms-of-use-modal">
			<BaseModal.Title
				onClose={handleReject}
				closeButtonTestId="terms-of-use-modal-close"
			>
				Terms Of Use
			</BaseModal.Title>
			<BaseModal.Body>
				<h3 className="typo-label-sm text-text-default">
					By continuing and using this Protocol
				</h3>
				<ul className="flex list-outside list-disc flex-col gap-y-lg">
					<li className="ml-lg">
						You understand and agree to the{' '}
						<Link
							href={Env.mainnetTermsUrl}
							className="inline"
							rightIcon="none"
							openInNewTab
						>
							Drift Terms and Conditions
						</Link>{' '}
						and{' '}
						<Link
							href={Env.mainnetDisclaimerUrl}
							className="inline"
							rightIcon="none"
							openInNewTab
						>
							Drift Protocol Disclaimer
						</Link>
						.
					</li>
					<li className="ml-lg">
						You understand leverage may change after entering a position.
					</li>
					<li className="ml-lg">
						You understand the rules and risks associated with settlement of
						P&L, bankruptcies, insurance, socialised losses and use of cross
						collateral as margin.
					</li>
					<li className="ml-lg">
						You understand your account may be partially or entirely liquidated
						if any position breaches margin maintenance requirements.
					</li>
				</ul>
			</BaseModal.Body>
			<BaseModal.Footer>
				<Button
					size="lg"
					variant="secondary"
					className="flex-1"
					onClick={handleReject}
					dataTestId="terms-of-use-modal-decline"
				>
					Decline
				</Button>
				<Button
					size="lg"
					className="flex-1"
					onClick={handleAccept}
					dataTestId="terms-of-use-modal-accept"
				>
					Accept
				</Button>
			</BaseModal.Footer>
		</BaseModal.Container>
	);
}
